/**
 * AudioRecorder
 *
 * Captures microphone audio, downsamples to 16kHz mono PCM16,
 * and emits base64-encoded chunks ready for Gemini Live API.
 * Exposes a `level` property (0–1) representing current mic volume.
 */

const TARGET_SAMPLE_RATE = 16000;
const BUFFER_SIZE = 4096;

export class AudioRecorder {
    private audioContext: AudioContext | null = null;
    private stream: MediaStream | null = null;
    private sourceNode: MediaStreamAudioSourceNode | null = null;
    private processorNode: ScriptProcessorNode | null = null;

    /** Current mic input level (0–1), updated each audio frame. */
    level = 0;

    onData: ((base64Chunk: string) => void) | null = null;

    async start(): Promise<void> {
        this.stream = await navigator.mediaDevices.getUserMedia({
            audio: {
                channelCount: 1,
                sampleRate: { ideal: TARGET_SAMPLE_RATE },
                echoCancellation: true,
                noiseSuppression: true,
            }
        });

        this.audioContext = new AudioContext();
        this.sourceNode = this.audioContext.createMediaStreamSource(this.stream);

        this.processorNode = this.audioContext.createScriptProcessor(BUFFER_SIZE, 1, 1);
        this.processorNode.onaudioprocess = (event) => {
            const inputData = event.inputBuffer.getChannelData(0);

            // Compute RMS level
            let sum = 0;
            for (let i = 0; i < inputData.length; i++) {
                sum += inputData[i] * inputData[i];
            }
            this.level = Math.min(1, Math.sqrt(sum / inputData.length) * 4);

            const sourceSampleRate = this.audioContext!.sampleRate;
            const pcm16 = downsampleAndEncode(inputData, sourceSampleRate, TARGET_SAMPLE_RATE);
            const base64 = arrayBufferToBase64(pcm16.buffer);
            this.onData?.(base64);
        };

        this.sourceNode.connect(this.processorNode);
        this.processorNode.connect(this.audioContext.destination);
    }

    stop(): void {
        this.processorNode?.disconnect();
        this.sourceNode?.disconnect();
        this.stream?.getTracks().forEach(t => t.stop());
        this.audioContext?.close();

        this.processorNode = null;
        this.sourceNode = null;
        this.stream = null;
        this.audioContext = null;
        this.level = 0;
    }
}

function downsampleAndEncode(
    input: Float32Array,
    sourceSampleRate: number,
    targetSampleRate: number
): Int16Array {
    const ratio = sourceSampleRate / targetSampleRate;
    const outputLength = Math.floor(input.length / ratio);
    const output = new Int16Array(outputLength);

    for (let i = 0; i < outputLength; i++) {
        const srcIndex = i * ratio;
        const low = Math.floor(srcIndex);
        const high = Math.min(low + 1, input.length - 1);
        const frac = srcIndex - low;
        const sample = input[low] * (1 - frac) + input[high] * frac;
        const clamped = Math.max(-1, Math.min(1, sample));
        output[i] = clamped < 0 ? clamped * 0x8000 : clamped * 0x7FFF;
    }

    return output;
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
}
